import React, { useState, useEffect } from 'react';
import {
  Card,
  CardContent,
  Typography,
  Box,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Chip,
  Button,
  Avatar,
  CircularProgress
} from '@mui/material';
import {
  People,
  LocalHospital,
  Phone,
  PersonAdd
} from '@mui/icons-material';
import { useAuth } from '../../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';

const API_URL = process.env.REACT_APP_API_URL;

interface Doctor {
  id: string;
  name: string;
  email: string;
  phone: string;
  specialization: string;
  department: string;
  status: string;
}

interface Patient {
  id: string;
  name: string;
  email: string;
  phone: string;
  age: number;
  gender: string;
  status: string;
}

export default function MarketExecutiveDashboard() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [doctors, setDoctors] = useState<Doctor[]>([]);
  const [patients, setPatients] = useState<Patient[]>([]);
  const [totalMarketUserDoctors, setTotalMarketUserDoctors] = useState(0);
  const [activeDoctors, setActiveDoctors] = useState(0);
  const [inactiveDoctors, setInactiveDoctors] = useState(0);
  const [totalPatients, setTotalPatients] = useState(0);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    console.log(localStorage.getItem('user'));
    const fetchData = async () => {
      await fetchCounts();
      setLoading(false);
    };
    fetchData();
  }, []);

  const fetchCounts = async () => {
    try {
      const token = localStorage.getItem('token');
      
      // Fetch market user specific doctors count
      const marketUserResponse = await fetch(`${API_URL}/marketing/market-user/doctors-count`, {
        method: 'GET',
        headers: {
          'accept': 'application/json',
          'Authorization': `Bearer ${token}`
        }
      });

      if (marketUserResponse.ok) {
        const data = await marketUserResponse.json();
        if (data.status === 'Success') {
          setTotalMarketUserDoctors(data.total_doctors);
          setActiveDoctors(data.active_doctors);
          setInactiveDoctors(data.inactive_doctors);
          setTotalPatients(data.total_patients);
        }
      }
    } catch (err) {
      console.error('Error fetching counts:', err);
    }
  };


  const StatCard = ({ title, value, icon, color, subtitle, onClick }: any) => (
    <Card
      elevation={2}
      sx={{
        cursor: onClick ? 'pointer' : 'default',
        '&:hover': onClick ? {
          boxShadow: 6,
          transform: 'translateY(-2px)',
          transition: 'all 0.3s ease'
        } : {}
      }}
      onClick={onClick}
    >
      <CardContent>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Box>
            <Typography color="textSecondary" gutterBottom variant="h6">
              {title}
            </Typography>
            <Typography variant="h4" component="h2" sx={{ color }}>
              {value}
            </Typography>
            {subtitle && (
              <Typography variant="body2" color="textSecondary">
                {subtitle}
              </Typography>
            )}
          </Box>
          <Box sx={{ color, opacity: 0.8 }}>
            {icon}
          </Box>
        </Box>
      </CardContent>
    </Card>
  );

 
  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ flexGrow: 1 }}>
      <Typography variant="h4" gutterBottom>
        Market Executive Dashboard
      </Typography>
      <Typography variant="subtitle1" color="textSecondary" gutterBottom>
        Welcome back, {user?.full_name || user?.username}
      </Typography>

      {/* Key Metrics */}
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: {
            xs: '1fr',
            sm: 'repeat(2, 1fr)',
            md: 'repeat(4, 1fr)'
          },
          gap: 3,
          mb: 4,
          mt: 3
        }}
      >
        <StatCard
          title="Total Doctors"
          value={totalMarketUserDoctors}
          icon={<LocalHospital fontSize="large" />}
          color="primary.main"
          subtitle="Your market doctors"
          onClick={() => navigate('/doctors')}
        />
        <StatCard
          title="Active Doctors"
          value={activeDoctors}
          icon={<LocalHospital fontSize="large" />}
          color="success.main"
          subtitle="Your active doctors"
        />
        <StatCard
          title="Inactive Doctors"
          value={inactiveDoctors}
          icon={<LocalHospital fontSize="large" />}
          color="warning.main"
          subtitle="Your inactive doctors"
        />
        <StatCard
          title="Total Patients"
          value={totalPatients}
          icon={<People fontSize="large" />}
          color="info.main"
          subtitle="Your market patients"
          onClick={() => navigate('/patients')}
        />
      </Box>

     
    </Box>
  );
}
